import { GameSession } from "./game-session.js";
import { SeededRandom, deriveSeed } from "./seeded-random.js";
import { phaseFor } from "../commentary/opponent-performance-director.js";
import { OpponentPolicy } from "../engine/opponent-policy.js";
import {
  AUTHORITATIVE_COMMAND_TYPES,
  LIMITS,
  failure,
  jsonClone,
  problem,
  sha256,
  validateActionShape,
} from "../agent/contracts.js";

export class MatchController {
  constructor(options) {
    this.data = options.data;
    this.engine = options.engine;
    this.session = options.session ?? new GameSession();
    this.config = normalizeConfig(options.config, this.data);
    this.seed = options.seed >>> 0;
    this.random = {
      opponent: new SeededRandom(
        deriveSeed(this.seed, "opponent"),
        options.randomStates?.opponent,
      ),
      presentation: new SeededRandom(
        deriveSeed(this.seed, "presentation"),
        options.randomStates?.presentation,
      ),
    };
    this.forcedResult = options.forcedResult ?? null;
    this.revision = Number.isInteger(options.revision) ? options.revision : 0;
    this.receiptSequence = Number.isInteger(options.receiptSequence)
      ? options.receiptSequence
      : 0;
    this.activity = Array.isArray(options.activity)
      ? jsonClone(options.activity).slice(-LIMITS.activityRetained)
      : [];
    this.policy = this.engine
      ? new OpponentPolicy(
          this.engine,
          this.data.openingBooks,
          this.random.opponent,
        )
      : null;
    this.phase = this.#derivePhase();
  }

  static create(options) {
    const seed = options.seed >>> 0;
    const config = normalizeConfig(options, options.data);
    if (options.playerSide === "random") {
      const sideRandom = new SeededRandom(deriveSeed(seed, "side"));
      config.playerSide = sideRandom.next() < 0.5 ? "w" : "b";
      if (options.perspectiveSide === undefined) {
        config.perspectiveSide = config.playerSide;
      }
    }
    const session = new GameSession();
    if (options.initialPgn) {
      session.loadPgn(options.initialPgn);
    }
    return new MatchController({
      data: options.data,
      engine: options.engine,
      session,
      seed,
      config,
    });
  }

  currentOpponent() {
    return (
      this.data.opponents.find((item) => item.id === this.config.opponentId) ??
      this.data.opponents[0]
    );
  }

  currentDifficulty() {
    return (
      this.data.difficulties.find(
        (item) => item.id === this.config.difficultyId,
      ) ?? this.data.difficulties[0]
    );
  }

  view() {
    return this.session.view();
  }

  status() {
    const view = this.view();
    const result = this.forcedResult?.result ?? view.status.result;
    const reason = this.forcedResult?.reason ?? view.status.reason;
    return {
      phase: this.phase,
      result,
      reason,
      gameOver: Boolean(this.forcedResult || view.status.gameOver),
      check: view.status.check,
    };
  }

  async validate(action) {
    const shape = validateActionShape(action);
    if (!shape.ok) {
      return shape;
    }
    const draft = withoutPlanHash(action);
    const availability = this.#validateAvailability(draft);
    if (!availability.ok) {
      return availability;
    }
    if (action.planHash) {
      const planned = await this.plan(draft);
      if (!planned.ok) {
        return planned;
      }
      if (planned.plan.planHash !== action.planHash) {
        return stalePlan(planned.plan.planHash, action.planHash);
      }
    }
    return {
      ok: true,
      valid: true,
      action: jsonClone(draft),
      requiresPlan: true,
      hints: [{ call: "plan", action: draft }],
    };
  }

  async plan(actionDraft) {
    const draft = withoutPlanHash(actionDraft);
    const shape = validateActionShape(draft);
    if (!shape.ok) {
      return shape;
    }
    const availability = this.#validateAvailability(draft);
    if (!availability.ok) {
      return availability;
    }
    const preview = this.#preview(draft, availability.legalMove);
    const planHash = await sha256({
      action: draft,
      preview,
      state: this.stateFingerprint(),
    });
    return { ok: true, plan: { ...preview, planHash } };
  }

  async act(action) {
    const shape = validateActionShape(action, { confirmation: true });
    if (!shape.ok) {
      return shape;
    }
    const draft = withoutPlanHash(action);
    const currentPlan = await this.plan(draft);
    if (!currentPlan.ok) {
      return action.planHash
        ? failure(
            "stale_plan",
            "The action can no longer be confirmed in the current position.",
            currentPlan.error.evidence,
            [
              { call: "findCandidates", request: { actionType: "move" } },
              { call: "inspect", requests: [{ view: "summary" }] },
            ],
          )
        : currentPlan;
    }
    if (currentPlan.plan.planHash !== action.planHash) {
      return stalePlan(currentPlan.plan.planHash, action.planHash);
    }
    if (draft.type === "move") {
      return this.#applyMove(draft, "player");
    }
    if (draft.type === "undo") {
      return this.#applyUndo(currentPlan.plan);
    }
    return this.#applyResignation(currentPlan.plan);
  }

  async advanceOne(options = {}) {
    if (this.phase !== "opponent_pending") {
      return failure(
        "decision_required",
        this.phase === "game_over"
          ? "The match is already complete."
          : "A player decision is required before the simulation can advance.",
        [problem("phase", "phase", "opponent_pending", this.phase)],
        [{ call: "inspect", requests: [{ view: "summary" }] }],
      );
    }
    if (!this.policy) {
      return failure(
        "engine_unavailable",
        "Computer play requires an opponent engine.",
        [problem("engine_unavailable", "engine", "available", "unavailable")],
      );
    }
    const before = this.positionFingerprint();
    const opponentRandomState = this.random.opponent.state;
    let selected;
    try {
      selected = await this.policy.chooseMove(
        this.session,
        this.currentOpponent(),
        this.currentDifficulty(),
      );
    } catch (error) {
      this.random.opponent.state = opponentRandomState;
      return failure("engine_unavailable", error.message, [
        problem("engine_unavailable", "engine", "legal move", error.message),
      ]);
    }
    if (options.beforeCommit) {
      const shouldCommit = await options.beforeCommit(jsonClone(selected));
      if (shouldCommit === false) {
        this.random.opponent.state = opponentRandomState;
        return failure(
          "stale_plan",
          "The pending opponent move was cancelled before commit.",
          [
            problem(
              "stale_plan",
              "position",
              before,
              this.positionFingerprint(),
            ),
          ],
        );
      }
    }
    if (
      before !== this.positionFingerprint() ||
      this.phase !== "opponent_pending"
    ) {
      this.random.opponent.state = opponentRandomState;
      return failure(
        "stale_plan",
        "The position changed before the opponent move committed.",
        [problem("stale_plan", "position", before, this.positionFingerprint())],
      );
    }
    return this.#applyMove(selected.move, "opponent", {
      source: selected.source,
    });
  }

  stateFingerprint() {
    return JSON.stringify({
      pgn: this.session.pgn(),
      fen: this.session.fen(),
      config: this.config,
      forcedResult: this.forcedResult,
      revision: this.revision,
      randomStates: {
        opponent: this.random.opponent.state,
        presentation: this.random.presentation.state,
      },
    });
  }

  positionFingerprint() {
    return JSON.stringify({
      pgn: this.session.pgn(),
      fen: this.session.fen(),
      config: this.config,
      forcedResult: this.forcedResult,
      revision: this.revision,
    });
  }

  state() {
    return jsonClone({
      pgn: this.session.pgn(),
      fen: this.session.fen(),
      config: this.config,
      seed: this.seed,
      randomStates: {
        opponent: this.random.opponent.state,
        presentation: this.random.presentation.state,
      },
      phase: this.phase,
      forcedResult: this.forcedResult,
      revision: this.revision,
      receiptSequence: this.receiptSequence,
    });
  }

  #validateAvailability(action) {
    const view = this.view();
    const status = this.status();
    if (action.type === "undo") {
      if (this.forcedResult) {
        return failure("game_over", "A resigned match cannot be undone.", [
          problem("game_over", "reason", "not resignation", "resignation"),
        ]);
      }
      if (!view.history.length) {
        return failure("nothing_to_undo", "There is no move to undo.", [
          problem("nothing_to_undo", "history.length", "> 0", 0),
        ]);
      }
      return { ok: true };
    }
    if (status.gameOver) {
      return failure("game_over", "The match is already complete.", [
        problem("game_over", "result", null, status.result),
      ]);
    }
    if (action.type === "resign") {
      return { ok: true };
    }
    if (this.phase !== "decision_required") {
      return failure(
        "not_your_turn",
        "The computer opponent must move first.",
        [problem("not_your_turn", "phase", "decision_required", this.phase)],
        [{ call: "advanceUntil", maxSteps: 1 }],
      );
    }
    if (
      this.config.mode === "computer" &&
      view.turn !== this.config.playerSide
    ) {
      return failure(
        "not_your_turn",
        "The requested piece is controlled by the opponent.",
        [problem("not_your_turn", "turn", this.config.playerSide, view.turn)],
      );
    }
    const legal = this.session
      .legalMoves(action.from)
      .filter((move) => move.to === action.to);
    if (legal.some((move) => move.promotion) && !action.promotion) {
      return failure(
        "promotion_required",
        "Promotion must specify queen, rook, bishop, or knight.",
        [
          problem(
            "promotion_required",
            "promotion",
            ["q", "r", "b", "n"],
            null,
          ),
        ],
        [
          {
            call: "findCandidates",
            request: { actionType: "move", from: action.from },
          },
        ],
      );
    }
    const legalMove = legal.find(
      (move) => (move.promotion ?? null) === (action.promotion ?? null),
    );
    if (!legalMove) {
      return failure(
        "illegal_move",
        `${action.from}-${action.to} is not legal in the current position.`,
        [
          problem(
            "illegal_move",
            "destination",
            "legal destination",
            action.to,
          ),
        ],
        [
          {
            call: "findCandidates",
            request: { actionType: "move", from: action.from },
          },
          { call: "inspect", requests: [{ view: "position" }] },
        ],
      );
    }
    return { ok: true, legalMove };
  }

  #preview(action, legalMove) {
    if (action.type === "undo") {
      return this.#previewUndo(action);
    }
    if (action.type === "resign") {
      const resigning =
        this.config.mode === "computer"
          ? this.config.playerSide
          : this.session.turn();
      return {
        action: jsonClone(action),
        costs: [],
        warnings: ["This immediately ends the match."],
        affected: { sides: [resigning], squares: [] },
        consequences: {
          result: resigning === "w" ? "0-1" : "1-0",
          reason: "resignation",
          gameOver: true,
        },
      };
    }
    const previewSession = new GameSession(this.session.pgn());
    const result = previewSession.applyMove(action);
    const affectedSquares = [legalMove.from, legalMove.to];
    if (legalMove.flags.includes("e")) {
      affectedSquares.push(`${legalMove.to[0]}${legalMove.from[1]}`);
    }
    if (legalMove.flags.includes("k")) {
      affectedSquares.push("h1", "f1");
    }
    if (legalMove.flags.includes("q")) {
      affectedSquares.push("a1", "d1");
    }
    if (legalMove.flags.includes("k") && legalMove.color === "b") {
      affectedSquares.splice(-2, 2, "h8", "f8");
    }
    if (legalMove.flags.includes("q") && legalMove.color === "b") {
      affectedSquares.splice(-2, 2, "a8", "d8");
    }
    const warnings = [];
    if (legalMove.captured) {
      warnings.push("This move captures a piece.");
    }
    if (legalMove.promotion) {
      warnings.push("This move promotes a pawn.");
    }
    if (result.view.status.gameOver) {
      warnings.push("This move ends the match.");
    }
    return {
      action: jsonClone(action),
      costs: [],
      warnings,
      affected: {
        squares: [...new Set(affectedSquares)].sort(),
        movingPiece: legalMove.piece,
        capturedPiece: legalMove.captured ?? null,
      },
      consequences: {
        san: result.move.san,
        capture: Boolean(result.move.captured),
        promotion: result.move.promotion ?? null,
        castle:
          result.move.flags.includes("k") || result.move.flags.includes("q"),
        check: result.view.status.check,
        gameOver: result.view.status.gameOver,
        result: result.view.status.result,
        reason: result.view.status.reason,
      },
    };
  }

  #previewUndo(action) {
    const history = this.view().history;
    const wasPending = this.phase === "opponent_pending";
    let plies = 1;
    const preview = new GameSession(this.session.pgn());
    preview.undo();
    if (
      this.config.mode === "computer" &&
      !wasPending &&
      preview.view().history.length > 0 &&
      preview.turn() !== this.config.playerSide
    ) {
      plies = 2;
    }
    const removed = history.slice(-plies).map((move) => move.san);
    return {
      action: jsonClone(action),
      costs: [],
      warnings: [
        `This removes ${plies} ${plies === 1 ? "ply" : "plies"} from history.`,
      ],
      affected: {
        squares: history
          .slice(-plies)
          .flatMap((move) => [move.from, move.to])
          .sort(),
      },
      consequences: { removedPlies: plies, removedMoves: removed },
    };
  }

  #applyMove(action, actor, metadata = {}) {
    const command = {
      type: "move",
      from: action.from,
      to: action.to,
      ...(action.promotion ? { promotion: action.promotion } : {}),
    };
    const previousPhase = phaseFor(this.view());
    const beforePly = this.view().history.length;
    const result = this.session.applyMove(command);
    this.forcedResult = null;
    this.revision += 1;
    this.phase = this.#derivePhase();
    const events = moveEvents(result.move, result.view, actor, previousPhase);
    const receipt = this.#receipt(
      command,
      actor,
      beforePly,
      result.view.history.length,
      events,
      metadata,
    );
    return { ok: true, receipt, status: this.status() };
  }

  #applyUndo(plan) {
    const beforePly = this.view().history.length;
    const removed = [];
    for (let index = 0; index < plan.consequences.removedPlies; index += 1) {
      const move = this.session.undo();
      if (move) {
        removed.unshift(move);
      }
    }
    this.forcedResult = null;
    this.revision += 1;
    this.phase = this.#derivePhase();
    const events = [
      {
        type: "undone",
        plies: removed.length,
        moves: removed.map((move) => move.san),
      },
    ];
    const receipt = this.#receipt(
      { type: "undo" },
      "player",
      beforePly,
      this.view().history.length,
      events,
    );
    return { ok: true, receipt, status: this.status() };
  }

  #applyResignation(plan) {
    const beforePly = this.view().history.length;
    this.forcedResult = {
      result: plan.consequences.result,
      reason: "resignation",
    };
    this.revision += 1;
    this.phase = "game_over";
    const events = [
      { type: "resigned", result: this.forcedResult.result },
      { type: "game_over", ...this.forcedResult },
    ];
    const receipt = this.#receipt(
      { type: "resign" },
      "player",
      beforePly,
      beforePly,
      events,
    );
    return { ok: true, receipt, status: this.status() };
  }

  #receipt(action, actor, startPly, endPly, events, metadata = {}) {
    this.receiptSequence += 1;
    const receipt = {
      id: `receipt_${String(this.receiptSequence).padStart(6, "0")}`,
      action: jsonClone(action),
      actor,
      startPly,
      endPly,
      events: jsonClone(events),
      ...metadata,
    };
    this.activity.push({ kind: "receipt", ...receipt });
    this.activity = this.activity.slice(-LIMITS.activityRetained);
    return receipt;
  }

  #derivePhase() {
    if (this.forcedResult || this.session.isGameOver()) {
      return "game_over";
    }
    if (
      this.config.mode === "computer" &&
      this.session.turn() !== this.config.playerSide
    ) {
      return "opponent_pending";
    }
    return "decision_required";
  }
}

function normalizeConfig(input = {}, data) {
  const mode = input.mode === "local" ? "local" : "computer";
  const requestedSide = input.playerSide ?? input.humanSide;
  return {
    mode,
    opponentId: data.opponents.some((item) => item.id === input.opponentId)
      ? input.opponentId
      : data.game.defaultOpponentId,
    difficultyId: data.difficulties.some(
      (item) => item.id === input.difficultyId,
    )
      ? input.difficultyId
      : data.game.defaultDifficultyId,
    playerSide: requestedSide === "b" ? "b" : "w",
    perspectiveSide:
      (input.perspectiveSide ?? requestedSide) === "b" ? "b" : "w",
  };
}

function withoutPlanHash(action) {
  if (!action || typeof action !== "object") {
    return action;
  }
  const { planHash: _planHash, ...draft } = action;
  return draft;
}

function stalePlan(current, provided) {
  return failure(
    "stale_plan",
    "The plan no longer matches the authoritative position.",
    [problem("stale_plan", "planHash", current, provided)],
    [{ call: "plan" }, { call: "inspect", requests: [{ view: "summary" }] }],
  );
}

function moveEvents(move, view, actor, previousPhase) {
  /** @type {any[]} */
  const events = [
    {
      type: "move",
      actor,
      from: move.from,
      to: move.to,
      san: move.san,
      piece: move.piece,
    },
  ];
  if (move.captured) {
    events.push({ type: "capture", piece: move.captured, at: move.to });
  }
  if (move.promotion) {
    events.push({ type: "promotion", piece: move.promotion, at: move.to });
  }
  if (move.flags.includes("k") || move.flags.includes("q")) {
    events.push({
      type: "castle",
      side: move.flags.includes("k") ? "king" : "queen",
    });
  }
  if (view.status.check) {
    events.push({ type: "check", side: view.turn });
  }
  const nextPhase = phaseFor(view);
  if (nextPhase !== previousPhase) {
    events.push({ type: "milestone", milestone: nextPhase });
  }
  if (view.status.gameOver) {
    events.push({
      type: "game_over",
      result: view.status.result,
      reason: view.status.reason,
    });
  }
  return events;
}

export function assertAuthoritativeCoverage() {
  return AUTHORITATIVE_COMMAND_TYPES.every(
    (type) =>
      type in
      {
        move: true,
        undo: true,
        resign: true,
      },
  );
}
